/**
 * WorkspaceModelProvider — ONE compile of the workspace, shared.
 *
 * The board (BoardPane → ArchitectureView) and the file/entity nav (WorkspaceNav)
 * both need the resolved model. Without this each ran its own `useModel`, so a
 * single workspace compiled twice on the main thread (plus the LSP worker).
 * This runs `useModel` once over the store's (debounced) files and hands the
 * result to both via context. The editor stays on live files + the LSP worker.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useModel } from "../hooks/useModel.js";
import { useFiles, useActiveFilePath, useWorkspace } from "./WorkspaceContext.js";
import { useActivePackage } from "./useActivePackage.js";
import { useOptionalQuietDiagnostics } from "./QuietDiagnosticsContext.js";
import {
  WorkspaceModelContext,
  filterToErrors,
  type WorkspaceModelApi,
} from "./WorkspaceModelContext.js";

// The model compiler resolves against a "/"-prefixed VFS (userRoot defaults to
// "/"). The viewer's store already keys files that way; the Studio host keys
// them bare (its draft/repo paths have no leading slash — see WorkspaceNav's
// toAbsolute note). Normalise here so EITHER convention compiles — the same
// store can be bare or "/"-prefixed and the board still resolves its modules.
// Idempotent for an already-absolute store, so the viewer is unaffected.
function toAbsoluteVfs(files: ReadonlyMap<string, string>): Map<string, string> {
  const out = new Map<string, string>();
  for (const [k, v] of files) out.set(k.startsWith("/") ? k : "/" + k, v);
  return out;
}

export interface WorkspaceModelProviderProps {
  /** Pin the package root the model resolves against (a VFS path). Omit for the
   *  default: the package that owns the active file (VSCode-style auto-follow),
   *  so the board shows whichever package the open tab belongs to. */
  readonly rootPath?: string;
  /** Follow the active file's package (default). The viewer composition turns
   *  this off — it renders the workspace's default root regardless of which
   *  file is "active" (there may be no editor at all). */
  readonly followActiveFile?: boolean;
  /** Debounce the recompile so a keystroke burst collapses into one solve. */
  readonly debounceMs?: number;
  readonly children: ReactNode;
}

export function WorkspaceModelProvider(props: WorkspaceModelProviderProps): ReactNode {
  const rawFiles = useFiles();
  const rawActiveFile = useActiveFilePath();
  // Compile against the "/"-prefixed VFS so a bare-keyed store (Studio) resolves.
  const files = useMemo(() => toAbsoluteVfs(rawFiles), [rawFiles]);
  const activeFile = rawActiveFile
    ? rawActiveFile.startsWith("/")
      ? rawActiveFile
      : "/" + rawActiveFile
    : rawActiveFile;
  // The shared model is scoped to the active package (the opaque unit of
  // resolution). `rootPath` follows the active file unless the host pins it
  // or opts out of following entirely.
  const followed = useActivePackage(files, activeFile, props.rootPath);
  const rootPath = props.followActiveFile === false ? props.rootPath : followed.rootPath;
  const store = useWorkspace();
  const [debounced, flushLatest] = useDebounced(files, props.debounceMs ?? 150);
  // `flushLatest` republishes the last-RENDERED files — but mutations call
  // flush() synchronously after a store write, BEFORE React re-renders, so
  // it would republish the pre-edit snapshot and the guard would stay stale
  // for the whole debounce window. Read the store imperatively instead.
  const flush = useCallback(
    () => flushLatest(toAbsoluteVfs(store.getState().files)),
    [flushLatest, store],
  );
  const model = useModel(debounced, rootPath);
  // A quiet surface (scratch) suppresses TODO + warnings at the EMISSION seam:
  // strip them from the one shared compile so every surface (board `!` badges,
  // minimap, nav, details) is clean — no per-representation filtering. Errors
  // still surface.
  const quiet = useOptionalQuietDiagnostics();
  const shared = useMemo<WorkspaceModelApi>(
    () => filterToErrors({ ...model, flush }, quiet),
    [quiet, model, flush],
  );
  return (
    <WorkspaceModelContext.Provider value={shared}>
      {props.children}
    </WorkspaceModelContext.Provider>
  );
}

// Debounce a value: returns the latest after `ms` of quiet, plus a `flush`
// that publishes NOW — either the explicit value passed in (a caller with a
// fresher source than the last render) or the latest rendered one.
function useDebounced<T>(value: T, ms: number): [T, (now?: T) => void] {
  const [debounced, setDebounced] = useState<T>(value);
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  const flush = useCallback((now?: T) => setDebounced(now ?? latest.current), []);
  return [debounced, flush];
}
